import type { ExcalidrawElement } from "./types";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/** a timestamp is "edited" only when it is at least this far from creation */
export const ELEMENT_EDITED_THRESHOLD_MS = 60 * 1000;

const toDate = (timestamp: number | null | undefined): Date | null => {
  if (
    timestamp === null ||
    timestamp === undefined ||
    !Number.isFinite(timestamp)
  ) {
    return null;
  }
  const date = new Date(timestamp);
  return Number.isNaN(date.getTime()) ? null : date;
};

const pad2 = (value: number) => String(value).padStart(2, "0");

/**
 * An absolute date label: short ("7 Sep") while the year is the current one,
 * so painting needs no clock and exports don't go stale. `null` when the
 * timestamp isn't usable. Fixed English in the viewer's local time zone — the
 * element package has no locale.
 */
export const formatDateLabel = (
  timestamp: number | null | undefined,
  { short = false, now = Date.now() }: { short?: boolean; now?: number } = {},
): string | null => {
  const date = toDate(timestamp);
  if (!date) {
    return null;
  }
  const label = `${date.getDate()} ${MONTHS[date.getMonth()]}`;
  const year = date.getFullYear();
  return short || year === new Date(now).getFullYear()
    ? label
    : `${label} ${year}`;
};

/** 24h "14:32" in the viewer's local time zone */
export const formatTimeLabel = (
  timestamp: number | null | undefined,
): string | null => {
  const date = toDate(timestamp);
  if (!date) {
    return null;
  }
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
};

const isSameDay = (a: number, b: number) => {
  const dateA = new Date(a);
  const dateB = new Date(b);
  return (
    dateA.getFullYear() === dateB.getFullYear() &&
    dateA.getMonth() === dateB.getMonth() &&
    dateA.getDate() === dateB.getDate()
  );
};

/**
 * The creation / modification label drawn under an element:
 *
 * - `7 Sep 14:32` — never edited (or edited within a minute of creation)
 * - `7 Sep 14:32 · edited 16:05` — edited the same day
 * - `7 Sep 14:32 · edited 8 Sep 09:10` — edited on another day
 * - `edited 7 Sep 14:32` — no creation time (legacy files)
 *
 * With `editedOnly` (sticky notes, whose footer already shows the creation
 * date) only the "edited" part is returned, or `null` when there is none.
 */
export const getElementTimestampLabel = (
  element: Pick<ExcalidrawElement, "created" | "updated">,
  {
    editedOnly = false,
    now = Date.now(),
  }: { editedOnly?: boolean; now?: number } = {},
): string | null => {
  const created = toDate(element.created) ? element.created : null;
  const updated = toDate(element.updated) ? element.updated : null;

  const fullLabel = (timestamp: number) =>
    `${formatDateLabel(timestamp, { now })} ${formatTimeLabel(timestamp)}`;

  if (created === null) {
    if (updated === null) {
      return null;
    }
    return `edited ${fullLabel(updated)}`;
  }

  const isEdited =
    updated !== null &&
    Math.abs(updated - created) >= ELEMENT_EDITED_THRESHOLD_MS;

  const editedLabel = isEdited
    ? `edited ${
        isSameDay(created, updated!)
          ? formatTimeLabel(updated)
          : fullLabel(updated!)
      }`
    : null;

  if (editedOnly) {
    return editedLabel;
  }

  return editedLabel
    ? `${fullLabel(created)} · ${editedLabel}`
    : fullLabel(created);
};
